/**
 * Mobile Drawer History Items Component
 *
 * Displays search history items in the mobile drawer using the same format as search results.
 * Provides consistent styling and interaction patterns.
 */

import React from "react";
import {
  Tag,
  Clock,
  Search,
  ExternalLink,
  X,
  Ticket,
  User,
  FileText,
  CheckSquare,
  MessageSquare,
  StickyNote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SearchHistoryItem } from "@wtc/ui";
import { Command, CommandList, CommandItem } from "@/components/ui/command";

// Simple icon mapping function - inline to avoid import issues
const getResultIcon = (type: string): React.ReactNode => {
  const iconClass = "h-4 w-4";

  switch (type) {
    case "doc":
      return <FileText className={`${iconClass} text-blue-600`} />;
    case "ticket":
      return <Ticket className={`${iconClass} text-blue-500`} />;
    case "contact":
      return <User className={`${iconClass} text-green-500`} />;
    case "time_entry":
      return <Clock className={`${iconClass} text-orange-500`} />;
    case "proposal":
      return <FileText className={`${iconClass} text-purple-500`} />;
    case "note":
      return <StickyNote className={`${iconClass} text-yellow-500`} />;
    case "task":
      return <CheckSquare className={`${iconClass} text-indigo-500`} />;
    case "message":
      return <MessageSquare className={`${iconClass} text-cyan-500`} />;
    case "template":
      return <FileText className={`${iconClass} text-pink-500`} />;
    default:
      return <FileText className={`${iconClass} text-gray-400`} />;
  }
};

interface MobileDrawerHistoryItemsProps {
  historyItems: SearchHistoryItem[];
  onItemClick: (item: SearchHistoryItem) => void;
  onRemoveItem?: (itemId: string) => void;
}

export function MobileDrawerHistoryItems({
  historyItems,
  onItemClick,
  onRemoveItem,
}: MobileDrawerHistoryItemsProps) {
  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleItemClick = (item: SearchHistoryItem) => {
    onItemClick(item);
    // Navigate to the URL
    window.location.href = item.url;
  };

  const handleRemoveItem = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    onRemoveItem?.(itemId);
  };

  if (historyItems.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Search className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p>No search history yet</p>
        <p className="text-sm text-gray-400 mt-1">Your recent searches will appear here</p>
      </div>
    );
  }

  return (
    <Command>
      <CommandList>
        {historyItems.map((item, index) => (
          <CommandItem
            key={item.id}
            onSelect={() => handleItemClick(item)}
            className="w-full px-4 py-3 hover:bg-card-50 rounded-lg text-left transition-all duration-200 transform active:scale-95 group"
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">{getResultIcon(item.type)}</div>
              <div className="flex-1 min-w-0">
                {/* Title */}
                <div className="text-sm font-medium text-card-foreground truncate">
                  {item.title}
                </div>

                {/* Subtitle */}
                {item.subtitle && (
                  <p className="text-xs text-gray-500 truncate mt-0.5">{item.subtitle}</p>
                )}

                {/* Type and Time */}
                <div className="flex items-center space-x-2 mt-1">
                  <Badge variant="outline" className="text-xs capitalize">
                    {item.type.replace("_", " ")}
                  </Badge>
                  <div className="flex items-center space-x-1 text-xs text-gray-400">
                    <Clock className="h-2.5 w-2.5" />
                    <span>{formatTimeAgo(item.timestamp)}</span>
                  </div>
                </div>

                {/* Search Query */}
                {item.searchQuery && (
                  <div className="flex items-center space-x-1 mt-1">
                    <Search className="h-2.5 w-2.5 text-gray-400" />
                    <span className="text-xs text-gray-400 italic">
                      &quot;{item.searchQuery}&quot;
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-shrink-0 flex items-center space-x-1">
                <ExternalLink className="h-3 w-3 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-400" />
                {onRemoveItem && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleRemoveItem(e, item.id)}
                    className="h-4 w-4 p-0 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-400 transition-all" title="Close"
                  >
                    <X className="h-2.5 w-2.5" />
                  </Button>
                )}
              </div>
            </div>
          </CommandItem>
        ))}
      </CommandList>
    </Command>
  );
}
